/**
 * 计算下拉/浮层相对触发器的视口位置（翻转 + maxHeight）
 */

import type { CSSProperties } from 'react'

export type FloatingBox = {
  /** 固定定位 top；placement=bottom 时有值 */
  top?: number
  /** 固定定位 bottom；placement=top 时有值（底边贴触发器上方） */
  bottom?: number
  /** 固定定位 left */
  left: number
  /** 与触发器同宽（可覆盖） */
  width: number
  /** 可用最大高度 */
  maxHeight: number
  /** 实际朝向 */
  placement: 'bottom' | 'top'
}

/**
 * @param trigger - 触发器 getBoundingClientRect()
 * @param gap - 与触发器间距
 * @param minWidth - 面板最小宽度；默认与触发器同宽
 */
export function computeDropdownBox(
  trigger: DOMRect,
  gap = 4,
  minWidth?: number,
): FloatingBox {
  const pad = 8
  const vw = window.innerWidth
  const vh = window.innerHeight
  const spaceBelow = vh - trigger.bottom - gap - pad
  const spaceAbove = trigger.top - gap - pad
  const placement: 'bottom' | 'top' =
    spaceBelow >= 160 || spaceBelow >= spaceAbove ? 'bottom' : 'top'
  const maxHeight = Math.max(96, placement === 'bottom' ? spaceBelow : spaceAbove)

  const width = Math.max(trigger.width, minWidth ?? trigger.width)
  let left = trigger.left
  if (left + width > vw - pad) left = Math.max(pad, vw - pad - width)
  if (left < pad) left = pad

  if (placement === 'bottom') {
    return {
      top: trigger.bottom + gap,
      left,
      width,
      maxHeight,
      placement,
    }
  }

  // 向上展开：用 bottom 锚定触发器顶边，避免按 maxHeight 推算 top 导致贴到视口顶部
  return {
    bottom: vh - trigger.top + gap,
    left,
    width,
    maxHeight,
    placement,
  }
}

/**
 * 将 FloatingBox 转为 fixed 面板 style
 * @param box - computeDropdownBox 结果
 */
export function floatingPanelStyle(box: FloatingBox): CSSProperties {
  return {
    left: box.left,
    width: box.width,
    maxHeight: box.maxHeight,
    top: box.placement === 'bottom' ? box.top : 'auto',
    bottom: box.placement === 'top' ? box.bottom : 'auto',
  }
}

export type TooltipBox = {
  top: number
  left: number
  placement: 'top' | 'bottom'
}

/**
 * Tooltip 定位：优先上方，空间不足则下方；水平居中并夹紧视口
 */
export function computeTooltipBox(
  trigger: DOMRect,
  tooltip: { width: number; height: number },
  gap = 6,
): TooltipBox {
  const pad = 8
  const vw = window.innerWidth
  const vh = window.innerHeight
  const spaceAbove = trigger.top - gap - pad
  const placement: 'top' | 'bottom' =
    spaceAbove >= tooltip.height ? 'top' : 'bottom'

  let left = trigger.left + trigger.width / 2 - tooltip.width / 2
  left = Math.min(Math.max(pad, left), vw - pad - tooltip.width)

  const top =
    placement === 'top'
      ? trigger.top - gap - tooltip.height
      : trigger.bottom + gap

  return {
    top: Math.min(Math.max(pad, top), vh - pad - tooltip.height),
    left,
    placement,
  }
}
